// Mention tokens in a chat message body. A mention is stored as
// `<@{uuid}|{name}>` inside the message body. The pattern matches the backend
// (`backend/app/services/chat/mentions.py`).

export type MentionPick = { id: string; name: string };

const TOKEN_SOURCE =
  '<@([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\\|([^|<>\\n]*)>';

// A new RegExp per call: a shared global instance would carry `lastIndex`
// between calls.
export function mentionRegex(): RegExp {
  return new RegExp(TOKEN_SOURCE, 'g');
}

// A token label never holds a pipe, an angle bracket or a newline.
export function cleanLabel(name: string): string {
  return name.replace(/[|<>\r\n]/g, '').trim();
}

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Turn each picked `@Full Name` into a token. A name that has no pick stays
// plain text. Longest names go first, and a letter or digit after the name
// blocks the match. Two picks with the same name take tokens in pick order, and
// extra mentions of that name reuse the last pick.
export function encodeMentions(text: string, picks: MentionPick[]): string {
  const queues = new Map<string, MentionPick[]>();
  for (const pick of picks) {
    if (!pick.name) continue;
    queues.set(pick.name, [...(queues.get(pick.name) ?? []), pick]);
  }
  if (queues.size === 0) return text;
  const names = [...queues.keys()].sort((a, b) => b.length - a.length);
  const pattern = new RegExp(
    `@(${names.map(escapeRegExp).join('|')})(?![\\p{L}\\p{N}])`,
    'gu'
  );
  return text.replace(pattern, (match, name: string) => {
    const queue = queues.get(name);
    // The last pick for a name stays, so a repeated mention of one person
    // encodes every time.
    const pick = queue && queue.length > 1 ? queue.shift() : queue?.[0];
    return pick ? `<@${pick.id}|${cleanLabel(pick.name)}>` : match;
  });
}

export function mentionsToText(body: string | null | undefined): string {
  if (!body) return '';
  return body.replace(
    mentionRegex(),
    (_match, _id, label: string) => `@${label}`
  );
}

export function decodeMentions(body: string): {
  text: string;
  picks: MentionPick[];
} {
  const picks = new Map<string, MentionPick>();
  for (const match of body.matchAll(mentionRegex())) {
    if (!picks.has(match[1]))
      picks.set(match[1], { id: match[1], name: match[2] });
  }
  return { text: mentionsToText(body), picks: [...picks.values()] };
}

export function mentionsUser(
  body: string | null | undefined,
  userId: string
): boolean {
  if (!body) return false;
  const id = userId.toLowerCase();
  for (const match of body.matchAll(mentionRegex())) {
    if (match[1] === id) return true;
  }
  return false;
}

// The `@query` that the caret sits in, or null. The `@` must start the text or
// follow whitespace. A whitespace character right after the `@`, or a newline in
// the query, opens nothing.
export function findMentionQuery(
  value: string,
  caret: number
): { start: number; query: string } | null {
  if (caret <= 0) return null;
  const start = value.lastIndexOf('@', caret - 1);
  if (start < 0) return null;
  if (start > 0 && !/\s/.test(value[start - 1])) return null;
  const query = value.slice(start + 1, caret);
  if (/^\s/.test(query) || query.includes('\n')) return null;
  return { start, query };
}
